import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { code: "compra", label: "Requisición de compra", hint: "Pedir la compra de un material/producto que aún no existe como OC" },
  { code: "gasto", label: "Gasto", hint: "Solicitar un gasto operativo (renta, servicios, marketing, etc.)" },
  { code: "reembolso", label: "Reembolso", hint: "Pedir que te regresen un gasto que pagaste de tu bolsillo" },
  { code: "anticipo", label: "Anticipo", hint: "Pedir dinero adelantado para un gasto futuro" },
  { code: "vacante", label: "Vacante", hint: "Solicitar autorización para abrir una vacante" },
];

const URGENCIAS = [
  { code: "baja", label: "Baja" },
  { code: "media", label: "Media" },
  { code: "alta", label: "Alta" },
  { code: "urgente", label: "Urgente" },
];

export default function OpsSolicitudNueva() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState("compra");
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id || "");
  const [submitting, setSubmitting] = useState(false);

  const [compra, setCompra] = useState({ item_description: "", quantity: "", unit: "", urgencia: "media", proveedor_sugerido: "", monto: "" });
  const [gasto, setGasto] = useState({ category: "", subcategory: "", vendor_name: "", vendor_rfc: "", description: "", subtotal: "", tax: "0", due_date: "" });
  const [motivoMonto, setMotivoMonto] = useState({ motivo: "", monto: "" });
  const [vacante, setVacante] = useState({ puesto: "", motivo: "", monto: "" });
  const [notes, setNotes] = useState("");

  const authorizedBranches = user?.branches_authorized || (user?.branch_id ? [user.branch_id] : []);
  const isMulti = authorizedBranches.length > 1;

  useEffect(() => {
    api.get("/ops/requests/types").then((r) => setCategories(r.data.expense_categories || [])).catch(() => {});
    if (isMulti) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [isMulti]);

  const gastoTotal = (parseFloat(gasto.subtotal) || 0) + (parseFloat(gasto.tax) || 0);

  const buildBody = () => {
    if (type === "compra") {
      if (!compra.item_description.trim() || !compra.quantity || !compra.unit.trim() || !compra.monto) {
        toast.error("Completa descripción, cantidad, unidad y monto estimado"); return null;
      }
      return {
        type, monto: parseFloat(compra.monto), branch_id: branchId || undefined, notes: notes || undefined,
        payload: {
          item_description: compra.item_description, quantity: parseFloat(compra.quantity),
          unit: compra.unit, urgencia: compra.urgencia, proveedor_sugerido: compra.proveedor_sugerido || undefined,
        },
      };
    }
    if (type === "gasto") {
      if (!gasto.category || !gasto.vendor_name.trim() || !gasto.description.trim() || !gasto.subtotal || !gasto.due_date) {
        toast.error("Completa categoría, proveedor, descripción, subtotal y fecha límite"); return null;
      }
      return {
        type, monto: gastoTotal, branch_id: branchId || undefined, notes: notes || undefined,
        payload: {
          category: gasto.category, subcategory: gasto.subcategory || undefined, vendor_name: gasto.vendor_name,
          vendor_rfc: gasto.vendor_rfc || undefined, description: gasto.description,
          subtotal: parseFloat(gasto.subtotal), tax: parseFloat(gasto.tax) || 0, due_date: gasto.due_date,
        },
      };
    }
    if (type === "reembolso" || type === "anticipo") {
      if (!motivoMonto.motivo.trim() || !motivoMonto.monto) {
        toast.error("Completa el motivo y el monto"); return null;
      }
      return {
        type, monto: parseFloat(motivoMonto.monto), branch_id: branchId || undefined, notes: notes || undefined,
        payload: { motivo: motivoMonto.motivo },
      };
    }
    if (type === "vacante") {
      if (!vacante.puesto.trim() || !vacante.motivo.trim()) {
        toast.error("Completa el puesto y el motivo"); return null;
      }
      return {
        type, monto: parseFloat(vacante.monto) || 0, branch_id: branchId || undefined, notes: notes || undefined,
        payload: { puesto: vacante.puesto, motivo: vacante.motivo },
      };
    }
    return null;
  };

  const submit = async () => {
    if (isMulti && !branchId) { toast.error("Selecciona una sucursal"); return; }
    const body = buildBody();
    if (!body) return;
    setSubmitting(true);
    try {
      const res = await api.post("/ops/requests", body);
      toast.success("Solicitud enviada a aprobación");
      navigate(`/ops/solicitudes/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la solicitud");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="ops-solicitud-nueva-page">
      <button onClick={() => navigate("/ops/solicitudes")} className="text-sm font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Volver al buzón
      </button>
      <h1 className="text-2xl font-bold text-slate-900">Nueva solicitud</h1>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Tipo de solicitud</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            {TYPES.map((t) => (
              <button key={t.code} onClick={() => setType(t.code)} data-testid={`tipo-solicitud-${t.code}`}
                className={`text-left p-3 rounded-lg border-2 transition-colors ${type === t.code ? "border-[#FFDF00] bg-yellow-50" : "border-slate-200 hover:border-slate-300"}`}>
                <div className="font-semibold text-sm text-slate-900">{t.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {isMulti && (
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Sucursal</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="select-sucursal-solicitud"
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Selecciona una sucursal</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}

        {type === "compra" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">¿Qué se necesita?</label>
              <input value={compra.item_description} onChange={(e) => setCompra({ ...compra, item_description: e.target.value })}
                data-testid="input-compra-descripcion" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej. Cajas de queso mozzarella" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Cantidad</label>
                <input type="number" value={compra.quantity} onChange={(e) => setCompra({ ...compra, quantity: e.target.value })}
                  data-testid="input-compra-cantidad" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Unidad</label>
                <input value={compra.unit} onChange={(e) => setCompra({ ...compra, unit: e.target.value })}
                  data-testid="input-compra-unidad" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="caja, kg, pza" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Urgencia</label>
                <select value={compra.urgencia} onChange={(e) => setCompra({ ...compra, urgencia: e.target.value })}
                  data-testid="select-compra-urgencia" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  {URGENCIAS.map((u) => <option key={u.code} value={u.code}>{u.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Monto estimado (MXN)</label>
                <input type="number" value={compra.monto} onChange={(e) => setCompra({ ...compra, monto: e.target.value })}
                  data-testid="input-compra-monto" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Proveedor sugerido (opcional)</label>
              <input value={compra.proveedor_sugerido} onChange={(e) => setCompra({ ...compra, proveedor_sugerido: e.target.value })}
                data-testid="input-compra-proveedor" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {type === "gasto" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Categoría</label>
                <select value={gasto.category} onChange={(e) => setGasto({ ...gasto, category: e.target.value, subcategory: "" })}
                  data-testid="select-gasto-categoria" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                  <option value="">Selecciona</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Proveedor / beneficiario</label>
                <input value={gasto.vendor_name} onChange={(e) => setGasto({ ...gasto, vendor_name: e.target.value })}
                  data-testid="input-gasto-proveedor" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Descripción</label>
              <input value={gasto.description} onChange={(e) => setGasto({ ...gasto, description: e.target.value })}
                data-testid="input-gasto-descripcion" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Subtotal</label>
                <input type="number" value={gasto.subtotal} onChange={(e) => setGasto({ ...gasto, subtotal: e.target.value })}
                  data-testid="input-gasto-subtotal" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Impuestos</label>
                <input type="number" value={gasto.tax} onChange={(e) => setGasto({ ...gasto, tax: e.target.value })}
                  data-testid="input-gasto-tax" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Total</label>
                <div className="mt-1 w-full border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-sm font-bold" data-testid="gasto-total-calculado">
                  ${gastoTotal.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Fecha límite de pago</label>
              <input type="date" value={gasto.due_date} onChange={(e) => setGasto({ ...gasto, due_date: e.target.value })}
                data-testid="input-gasto-fecha" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {(type === "reembolso" || type === "anticipo") && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Motivo</label>
              <textarea value={motivoMonto.motivo} onChange={(e) => setMotivoMonto({ ...motivoMonto, motivo: e.target.value })}
                data-testid="input-motivo-solicitud" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={3} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Monto (MXN)</label>
              <input type="number" value={motivoMonto.monto} onChange={(e) => setMotivoMonto({ ...motivoMonto, monto: e.target.value })}
                data-testid="input-monto-solicitud" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        )}

        {type === "vacante" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Puesto</label>
              <input value={vacante.puesto} onChange={(e) => setVacante({ ...vacante, puesto: e.target.value })}
                data-testid="input-vacante-puesto" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Motivo / justificación</label>
              <textarea value={vacante.motivo} onChange={(e) => setVacante({ ...vacante, motivo: e.target.value })}
                data-testid="input-vacante-motivo" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={3} />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Salario estimado (opcional, MXN)</label>
              <input type="number" value={vacante.monto} onChange={(e) => setVacante({ ...vacante, monto: e.target.value })}
                data-testid="input-vacante-monto" className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              Al aprobarse queda como "Vacante autorizada" — el pipeline de reclutamiento todavía no existe en el sistema.
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Notas adicionales (opcional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="input-notas-solicitud"
            className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} />
        </div>

        <button onClick={submit} disabled={submitting} data-testid="btn-enviar-solicitud"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors disabled:opacity-50">
          <Send className="w-4 h-4" /> {submitting ? "Enviando..." : "Enviar a aprobación"}
        </button>
      </div>
    </div>
  );
}
